<template>
  <div class="ai-config">

    <!-- Header com toggle -->
    <div class="ai-header">
      <div class="ai-header-left">
        <div class="ai-header-icon">🤖</div>
        <div>
          <div class="ai-header-title">Inteligencia Artificial</div>
          <div class="ai-header-sub">Ative a IA para responder visitantes automaticamente</div>
        </div>
      </div>
      <label class="ai-toggle">
        <input type="checkbox" v-model="enabled" />
        <span class="ai-toggle-track"><span class="ai-toggle-thumb"></span></span>
        <span class="ai-toggle-label">{{ enabled ? 'Ativo' : 'Inativo' }}</span>
      </label>
    </div>

    <template v-if="enabled">

      <!-- Motor de IA -->
      <div class="ai-section">
        <div class="ai-section-title">Motor de IA</div>
        <div class="ai-engine-card">
          <div class="ai-engine-icon">⚡</div>
          <div class="ai-engine-info">
            <div class="ai-engine-name">Booster IA</div>
            <div class="ai-engine-desc">Motor de inteligencia artificial otimizado para conversao e atendimento. Respostas rapidas e naturais em portugues.</div>
          </div>
          <div class="ai-engine-badge">Incluso</div>
        </div>

        <!-- Chave propria (apenas super admin) -->
        <template v-if="isSuperAdmin">
        <div class="ai-advanced-toggle" @click="showAdvanced = !showAdvanced">
          <feather-icon :icon="showAdvanced ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="14" />
          <span>Configuracao avancada (admin)</span>
        </div>
        <div v-if="showAdvanced" class="ai-advanced-body">
          <p class="ai-hint" style="margin-bottom:12px">Se preferir, use sua propria chave de API. Deixe em branco para usar o Booster IA incluso no plano.</p>
          <div class="ai-row">
            <div class="ai-field">
              <label class="ai-label">Provedor</label>
              <select v-model="config.ai_provider" class="ai-select">
                <option value="anthropic">Anthropic (Claude)</option>
                <option value="openai">OpenAI (ChatGPT)</option>
              </select>
            </div>
            <div class="ai-field">
              <label class="ai-label">Modelo</label>
              <select v-model="config.ai_model" class="ai-select">
                <template v-if="config.ai_provider === 'anthropic'">
                  <option value="claude-haiku-4-5-20251001">Claude Haiku 4.5</option>
                </template>
                <template v-else>
                  <option value="gpt-4o-mini">GPT-4o Mini</option>
                  <option value="gpt-4o">GPT-4o</option>
                </template>
              </select>
            </div>
          </div>
          <div class="ai-field">
            <label class="ai-label">API Key</label>
            <input v-model="config.ai_api_key" type="password" class="ai-input" placeholder="sk-..." />
          </div>
        </div>
        </template>
      </div>

      <!-- Instrucoes do Especialista -->
      <div class="ai-section">
        <div class="ai-section-title-lg">
          <span class="ai-section-icon">⚙</span>
          Instrucoes do Especialista
        </div>
        <p class="ai-section-desc">Defina o conhecimento e as regras de comportamento deste agente. Essas instrucoes sao enviadas como contexto para a IA em cada conversa.</p>
        <div class="ai-field">
          <textarea v-model="config.ai_instructions" class="ai-textarea ai-textarea--lg" rows="12"
            placeholder="Ex: Voce e especialista em [seu produto/servico].

Conhecimentos:
- Produto: [descreva o que vende]
- Precos: [liste os planos e valores]
- Diferenciais: [o que te diferencia]
- Garantia: [politica de garantia]
- Pagamento: [formas aceitas]

Regras:
- Sempre pergunte o nome do visitante
- Ofereca desconto apenas se o visitante hesitar
- Direcione para o link de compra quando demonstrar interesse
- Nunca fale mal de concorrentes"></textarea>
          <p class="ai-hint">Use markdown para organizar. Quanto mais especifico, melhor a qualidade das respostas.</p>
        </div>
      </div>

      <!-- Exemplos de Conversa -->
      <div class="ai-section">
        <div class="ai-section-title-lg">
          <span class="ai-section-icon">💬</span>
          Exemplos de Conversa
        </div>
        <p class="ai-section-desc">Ensine o tom e estilo de resposta do agente. A IA aprende a imitar esses padroes.</p>
        <div class="ai-field">
          <textarea v-model="config.ai_examples" class="ai-textarea ai-textarea--lg" rows="10"
            placeholder="Visitante: Quanto custa?
Agente: opa! o [produto] ta saindo por R$497 mas hj tem desconto de 30% 🔥

Visitante: Tem garantia?
Agente: tem sim! 7 dias pra vc testar sem compromisso

Visitante: Aceita PIX?
Agente: aceita sim! pix, cartao ou boleto 😉 pelo pix sai mais barato. quer o link?

Visitante: To em duvida ainda
Agente: entendo! o que ta te travando? posso te ajudar a decidir 💪"></textarea>
          <p class="ai-hint">Quanto mais exemplos reais, mais natural o agente responde. Recomendamos pelo menos 5 exemplos.</p>
        </div>
      </div>

      <!-- Campos de captura do lead -->
      <div class="ai-section">
        <div class="ai-section-title">📋 Informacoes para capturar do lead</div>
        <p class="ai-hint" style="margin-bottom:14px">
          O agente vai extrair essas informacoes de forma natural durante a conversa, usando perguntas estrategicas. Quando capturar tudo, salva automaticamente como lead.
        </p>

        <div class="ai-fields-list">
          <div v-for="(field, i) in captureFields" :key="i" class="ai-capture-field">
            <div class="ai-capture-field__info">
              <input v-model="field.label" class="ai-capture-field__label" placeholder="Nome do campo" />
              <input v-model="field.placeholder" class="ai-capture-field__placeholder" placeholder="Ex: Nome completo, WhatsApp com DDD..." />
            </div>
            <div class="ai-capture-field__actions">
              <label class="ai-capture-field__required" :title="field.required ? 'Obrigatorio' : 'Opcional'">
                <input type="checkbox" v-model="field.required" />
                <span>{{ field.required ? 'Obrigatorio' : 'Opcional' }}</span>
              </label>
              <button class="ai-capture-field__remove" @click="removeCaptureField(i)" title="Remover campo">
                <feather-icon icon="XIcon" size="14" />
              </button>
            </div>
          </div>
        </div>

        <button class="ai-add-field-btn" @click="addCaptureField">
          <feather-icon icon="PlusIcon" size="14" />
          Adicionar campo
        </button>
      </div>

      <!-- Configuracoes avancadas (apenas super admin) -->
      <template v-if="isSuperAdmin">
      <div class="ai-section">
        <div class="ai-section-title">Configuracoes avancadas</div>
        <div class="ai-row">
          <div class="ai-field">
            <label class="ai-label">Max tokens (tamanho da resposta)</label>
            <input v-model.number="config.ai_max_tokens" type="number" class="ai-input" min="50" max="1000" />
          </div>
          <div class="ai-field">
            <label class="ai-label">Temperatura (criatividade: 0.0 a 1.0)</label>
            <input v-model.number="config.ai_temperature" type="number" class="ai-input" min="0" max="1" step="0.1" />
          </div>
        </div>
      </div>
      </template>

      <!-- Botao salvar -->
      <div class="ai-actions">
        <button class="ai-btn-save" @click="salvar" :disabled="salvando">
          <svg v-if="!salvando" width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
          <span v-if="salvando" class="ai-spinner"></span>
          {{ salvando ? 'Salvando...' : 'Salvar configuracoes' }}
        </button>
      </div>

      <!-- Teste -->
      <div class="ai-section ai-test-section">
        <div class="ai-section-title">🧪 Testar o agente</div>
        <p class="ai-hint" style="margin-bottom:12px">Salve as configuracoes antes de testar. Converse com seu agente para validar o comportamento.</p>
        <div class="ai-test-chat" ref="testChat">
          <div v-if="!testMessages.length" class="ai-test-empty">Envie uma mensagem para iniciar o teste</div>
          <div v-for="(msg, i) in testMessages" :key="i" class="ai-test-msg" :class="'ai-test-' + msg.role">
            {{ msg.content }}
          </div>
          <div v-if="testando" class="ai-test-msg ai-test-assistant ai-test-typing">
            <span class="ai-dot"></span><span class="ai-dot"></span><span class="ai-dot"></span>
          </div>
        </div>
        <div class="ai-test-input-row">
          <input v-model="testInput" class="ai-input ai-test-input" placeholder="Digite uma mensagem para testar..." @keyup.enter="testar" :disabled="testando" />
          <button class="ai-btn-test" @click="testar" :disabled="testando || !testInput.trim()">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
        </div>
      </div>

    </template>

    <!-- Estado desativado -->
    <div v-else class="ai-disabled">
      <div class="ai-disabled-icon">🤖</div>
      <p class="ai-disabled-title">IA desativada</p>
      <p class="ai-disabled-sub">Ative a inteligencia artificial para que o chat responda seus visitantes automaticamente com linguagem natural, quebre objecoes e capture leads.</p>
    </div>

  </div>
</template>

<script>
export default {
  name: 'ChatAiConfig',
  props: {
    chatId: { type: String, default: '' },
  },
  data() {
    return {
      enabled: false,
      config: {
        ai_enabled: 0,
        ai_provider: 'anthropic',
        ai_model: 'claude-haiku-4-5-20251001',
        ai_api_key: '',
        ai_instructions: '',
        ai_examples: '',
        ai_max_tokens: 300,
        ai_temperature: 0.7,
      },
      showAdvanced: false,
      salvando: false,
      captureFields: [
        { label: 'Nome', placeholder: 'Nome completo do visitante', required: true },
        { label: 'Email', placeholder: 'E-mail para contato', required: true },
        { label: 'WhatsApp', placeholder: 'Numero com DDD', required: true },
      ],
      testando: false,
      testInput: '',
      testMessages: [],
    }
  },
  computed: {
    isSuperAdmin() {
      try {
        const user = JSON.parse(localStorage.getItem('userData') || '{}')
        return user.user_email === 'edvomberto@gmail.com'
      } catch { return false }
    },
  },
  watch: {
    enabled(v) { this.config.ai_enabled = v ? 1 : 0 },
  },
  mounted() {
    this.carregar()
  },
  methods: {
    api() { return process.env.VUE_APP_HOST_API_BACKEND || '/api/dkbackend' },

    addCaptureField() {
      this.captureFields.push({ label: '', placeholder: '', required: false })
    },
    removeCaptureField(i) {
      if (this.captureFields.length <= 1) return
      this.captureFields.splice(i, 1)
    },

    async carregar() {
      if (!this.chatId) return
      try {
        const resp = await fetch(`${this.api()}/chat-ai/${this.chatId}`)
        const data = await resp.json()
        if (data && data.chat_id) {
          this.config = { ...this.config, ...data }
          this.enabled = !!data.ai_enabled
          if (data.ai_capture_fields) {
            try { this.captureFields = JSON.parse(data.ai_capture_fields) } catch {}
          }
        }
      } catch (e) {
        console.error('[ChatAI] Erro ao carregar:', e)
      }
    },

    async salvar() {
      if (!this.chatId) {
        this.$message({ type: 'error', message: 'Chat ID nao encontrado' })
        return
      }
      this.salvando = true
      try {
        const payload = {
          ...this.config,
          ai_capture_fields: JSON.stringify(this.captureFields),
        }
        const resp = await fetch(`${this.api()}/chat-ai/${this.chatId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        })
        const data = await resp.json()
        if (resp.ok) {
          this.$message({ type: 'success', message: 'Configuracoes da IA salvas com sucesso' })
        } else {
          this.$message({ type: 'error', message: data.error || 'Falha ao salvar' })
        }
      } catch (e) {
        console.error('[ChatAI] Erro ao salvar:', e)
        this.$message({ type: 'error', message: 'Erro ao salvar: ' + e.message })
      } finally {
        this.salvando = false
      }
    },

    async testar() {
      if (!this.testInput.trim() || this.testando) return
      const msg = this.testInput.trim()
      this.testInput = ''
      this.testMessages.push({ role: 'user', content: msg })
      this.testando = true
      this.scrollTestChat()
      try {
        const resp = await fetch(`${this.api()}/chat-ai/respond`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            chat_id: this.chatId,
            visitor_message: msg,
            conversation_history: this.testMessages.filter(m => m.role !== 'typing'),
          }),
        })
        const data = await resp.json()
        if (data.messages && data.messages.length) {
          // Simula digitacao humana - adiciona cada paragrafo com delay
          for (let i = 0; i < data.messages.length; i++) {
            if (i > 0) await this.delay(600 + Math.random() * 800)
            this.testMessages.push({ role: 'assistant', content: data.messages[i] })
            this.scrollTestChat()
          }
        } else if (data.error) {
          this.testMessages.push({ role: 'assistant', content: '⚠️ ' + data.error })
        }
      } catch (e) {
        this.testMessages.push({ role: 'assistant', content: '⚠️ Erro de conexao: ' + e.message })
      } finally {
        this.testando = false
        this.scrollTestChat()
      }
    },

    delay(ms) { return new Promise(r => setTimeout(r, ms)) },

    scrollTestChat() {
      this.$nextTick(() => {
        const el = this.$refs.testChat
        if (el) el.scrollTop = el.scrollHeight
      })
    },
  },
}
</script>

<style scoped>
.ai-config { max-width: 700px; }

/* Header */
.ai-header { display: flex; align-items: center; justify-content: space-between; padding: 20px; background: rgba(232,106,16,0.06); border: 1px solid rgba(232,106,16,0.15); border-radius: 14px; margin-bottom: 24px; gap: 16px; flex-wrap: wrap; }
.ai-header-left { display: flex; align-items: center; gap: 12px; }
.ai-header-icon { font-size: 28px; }
.ai-header-title { font-size: 15px; font-weight: 700; color: var(--dk-text-primary, #e2e8f0); }
.ai-header-sub { font-size: 12px; color: var(--dk-text-muted, #8a94a6); margin-top: 2px; }

/* Toggle */
.ai-toggle { display: flex; align-items: center; gap: 8px; cursor: pointer; flex-shrink: 0; }
.ai-toggle input { display: none; }
.ai-toggle-track { width: 42px; height: 24px; border-radius: 12px; background: rgba(255,255,255,0.1); position: relative; transition: background 0.2s; }
.ai-toggle input:checked + .ai-toggle-track { background: #E86A10; }
.ai-toggle-thumb { position: absolute; top: 2px; left: 2px; width: 20px; height: 20px; border-radius: 50%; background: #fff; transition: transform 0.2s; box-shadow: 0 1px 4px rgba(0,0,0,0.3); }
.ai-toggle input:checked + .ai-toggle-track .ai-toggle-thumb { transform: translateX(18px); }
.ai-toggle-label { font-size: 12px; font-weight: 600; color: var(--dk-text-muted, #8a94a6); }

/* Sections */
.ai-section { margin-bottom: 24px; }
.ai-section-title { font-size: 12px; font-weight: 700; color: #E86A10; margin-bottom: 12px; text-transform: uppercase; letter-spacing: 0.5px; }
.ai-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.ai-field { margin-bottom: 14px; }
.ai-label { display: block; font-size: 12px; font-weight: 600; color: var(--dk-text-primary, #e2e8f0); margin-bottom: 6px; }
.ai-hint { font-size: 11px; color: var(--dk-text-muted, #64748b); margin-top: 4px; }

/* Section title large */
.ai-section-title-lg {
  display: flex; align-items: center; gap: 8px;
  font-size: 16px; font-weight: 800; color: var(--dk-text-primary, #e2e8f0);
  margin-bottom: 8px;
}
.ai-section-icon { font-size: 18px; }
.ai-section-desc { font-size: 13px; color: var(--dk-text-muted, #8a94a6); line-height: 1.5; margin-bottom: 16px; }
.ai-textarea--lg {
  min-height: 200px;
  font-family: 'Fira Code', 'Consolas', 'Courier New', monospace;
  font-size: 12.5px;
  line-height: 1.7;
  border: 2px solid rgba(255,255,255,0.08);
  border-radius: 10px;
  padding: 16px;
}
.ai-textarea--lg:focus { border-color: #E86A10; }

/* Capture fields */
.ai-fields-list { display: flex; flex-direction: column; gap: 8px; margin-bottom: 12px; }
.ai-capture-field {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 14px; border-radius: 8px;
  background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08);
  transition: border-color 0.2s;
}
.ai-capture-field:hover { border-color: rgba(255,255,255,0.15); }
.ai-capture-field__info { flex: 1; display: flex; flex-direction: column; gap: 4px; }
.ai-capture-field__label {
  background: none; border: none; color: var(--dk-text-primary, #e2e8f0);
  font-size: 13px; font-weight: 600; padding: 0; outline: none; width: 100%;
}
.ai-capture-field__label::placeholder { color: var(--dk-text-muted, #64748b); }
.ai-capture-field__placeholder {
  background: none; border: none; color: var(--dk-text-muted, #8a94a6);
  font-size: 11px; padding: 0; outline: none; width: 100%;
}
.ai-capture-field__placeholder::placeholder { color: rgba(138,148,166,0.4); }
.ai-capture-field__actions { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.ai-capture-field__required { display: flex; align-items: center; gap: 4px; font-size: 10px; color: var(--dk-text-muted, #8a94a6); cursor: pointer; white-space: nowrap; }
.ai-capture-field__required input { accent-color: #E86A10; }
.ai-capture-field__remove {
  width: 26px; height: 26px; border-radius: 6px; border: none; cursor: pointer;
  background: rgba(239,68,68,0.08); color: #EF4444;
  display: flex; align-items: center; justify-content: center; transition: all 0.15s;
}
.ai-capture-field__remove:hover { background: rgba(239,68,68,0.2); }
.ai-add-field-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 16px; border-radius: 8px; border: 1.5px dashed rgba(232,106,16,0.3);
  background: rgba(232,106,16,0.04); color: #E86A10;
  font-size: 12px; font-weight: 600; cursor: pointer; font-family: inherit;
  transition: all 0.2s;
}
.ai-add-field-btn:hover { background: rgba(232,106,16,0.1); border-color: rgba(232,106,16,0.5); }

/* Engine card */
.ai-engine-card {
  display: flex; align-items: center; gap: 14px;
  padding: 16px; border-radius: 10px;
  background: rgba(232,106,16,0.06); border: 1.5px solid rgba(232,106,16,0.2);
  margin-bottom: 12px;
}
.ai-engine-icon { font-size: 24px; flex-shrink: 0; }
.ai-engine-info { flex: 1; }
.ai-engine-name { font-size: 14px; font-weight: 700; color: var(--dk-text-primary, #e2e8f0); }
.ai-engine-desc { font-size: 11px; color: var(--dk-text-muted, #8a94a6); margin-top: 2px; line-height: 1.4; }
.ai-engine-badge {
  padding: 4px 12px; border-radius: 20px; font-size: 11px; font-weight: 700;
  background: rgba(16,185,129,0.12); color: #10B981; flex-shrink: 0;
}

/* Advanced toggle */
.ai-advanced-toggle {
  display: flex; align-items: center; gap: 6px;
  font-size: 12px; color: var(--dk-text-muted, #64748b); cursor: pointer;
  padding: 8px 0; transition: color 0.2s;
}
.ai-advanced-toggle:hover { color: var(--dk-text-primary, #e2e8f0); }
.ai-advanced-body {
  padding: 14px; background: rgba(255,255,255,0.02);
  border: 1px solid rgba(255,255,255,0.06); border-radius: 8px; margin-top: 8px;
}

/* Inputs */
.ai-input, .ai-select {
  width: 100%; padding: 10px 14px;
  background: rgba(255,255,255,0.04); border: 1.5px solid rgba(255,255,255,0.1);
  border-radius: 8px; color: var(--dk-text-primary, #e2e8f0);
  font-size: 13px; font-family: inherit; outline: none; transition: border-color 0.2s;
}
.ai-input:focus, .ai-select:focus { border-color: #E86A10; }
.ai-select { cursor: pointer; appearance: auto; }
.ai-textarea {
  width: 100%; padding: 12px 14px;
  background: rgba(255,255,255,0.04); border: 1.5px solid rgba(255,255,255,0.1);
  border-radius: 8px; color: var(--dk-text-primary, #e2e8f0);
  font-size: 12px; font-family: inherit; line-height: 1.6;
  resize: vertical; outline: none; transition: border-color 0.2s;
}
.ai-textarea:focus { border-color: #E86A10; }

/* Actions */
.ai-actions { margin: 24px 0; }
.ai-btn-save {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 12px 28px; border-radius: 10px; border: none;
  background: linear-gradient(135deg, #E86A10, #d45e0c); color: #fff;
  font-size: 13px; font-weight: 700; cursor: pointer; font-family: inherit;
  box-shadow: 0 4px 14px rgba(232,106,16,0.3); transition: all 0.2s;
}
.ai-btn-save:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(232,106,16,0.4); }
.ai-btn-save:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
.ai-spinner {
  width: 14px; height: 14px; border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.3); border-top-color: #fff;
  animation: ai-spin 0.6s linear infinite;
}
@keyframes ai-spin { to { transform: rotate(360deg); } }

/* Test Chat */
.ai-test-section { background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.06); border-radius: 14px; padding: 20px; }
.ai-test-chat { min-height: 100px; max-height: 320px; overflow-y: auto; display: flex; flex-direction: column; gap: 8px; margin-bottom: 12px; padding: 4px 0; }
.ai-test-empty { text-align: center; color: var(--dk-text-muted, #64748b); font-size: 12px; padding: 32px 0; }
.ai-test-msg { padding: 10px 14px; border-radius: 14px; font-size: 13px; line-height: 1.5; max-width: 80%; word-break: break-word; }
.ai-test-user { background: #E86A10; color: #fff; align-self: flex-end; border-bottom-right-radius: 4px; }
.ai-test-assistant { background: rgba(232,106,16,0.12); color: var(--dk-text-primary, #e2e8f0); align-self: flex-start; border-bottom-left-radius: 4px; }
.ai-test-typing { display: flex; gap: 4px; padding: 12px 18px; }
.ai-dot { width: 6px; height: 6px; border-radius: 50%; background: #E86A10; animation: ai-bounce 1.2s ease-in-out infinite; }
.ai-dot:nth-child(2) { animation-delay: 0.15s; }
.ai-dot:nth-child(3) { animation-delay: 0.3s; }
@keyframes ai-bounce { 0%, 80%, 100% { transform: translateY(0); } 40% { transform: translateY(-6px); } }

.ai-test-input-row { display: flex; gap: 8px; }
.ai-test-input { flex: 1; }
.ai-btn-test {
  width: 42px; height: 42px; border-radius: 10px; border: none;
  background: #E86A10; color: #fff; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.2s; flex-shrink: 0;
}
.ai-btn-test:hover:not(:disabled) { background: #d45e0c; }
.ai-btn-test:disabled { opacity: 0.4; cursor: not-allowed; }

/* Disabled state */
.ai-disabled { text-align: center; padding: 48px 24px; }
.ai-disabled-icon { font-size: 48px; margin-bottom: 16px; opacity: 0.3; }
.ai-disabled-title { font-size: 16px; font-weight: 700; color: var(--dk-text-primary, #e2e8f0); margin-bottom: 8px; }
.ai-disabled-sub { font-size: 13px; color: var(--dk-text-muted, #8a94a6); line-height: 1.6; max-width: 400px; margin: 0 auto; }

@media (max-width: 600px) {
  .ai-row { grid-template-columns: 1fr; }
  .ai-header { flex-direction: column; align-items: flex-start; }
}
</style>
